import { Suspense, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useScroll } from "@react-three/drei";
import { EffectComposer, Bloom, Vignette } from "@react-three/postprocessing";
import * as THREE from "three";
import { SCENE_COUNT } from "./constants";

import Scene01Birth from "./scenes/Scene01Birth";

// Camera is parked. Every concept lives at the origin and only one is "present"
// at a time. As you scroll, the current slide fades + scales out while the next
// fades + scales in (a cross-fade), instead of flying the camera through a tunnel.
// Slide 0 is special: it zooms in as you scroll into it before handing off.

const GLOBAL_SCALE = 0.62; // concepts sit smaller, leaving room for the centred top text
const MOBILE_SCALE = 0.5; // narrow viewports need everything pulled in further to fit

const smoothstep = (t) => {
  t = THREE.MathUtils.clamp(t, 0, 1);
  return t * t * (3 - 2 * t);
};

function Slide({ index, scroll, children }) {
  const ref = useRef();
  const baseOpacity = useRef(new WeakMap());
  const { size } = useThree();
  const globalScale = size.width < 768 ? MOBILE_SCALE : GLOBAL_SCALE;

  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    const slidePos = scroll.offset * SCENE_COUNT;

    // presence: 1 across the core of the slide's slot, ramping to exactly 0 at the
    // slot boundary (FADE = ramp width). Because each slide hits 0 right at the
    // boundary, the outgoing slide is gone before the incoming one appears — no
    // two slides are visible at once, and there's no blank gap between them.
    const FADE = 0.25;
    const presence =
      index === 0
        ? slidePos < 0.75
          ? 1
          : smoothstep((1 - slidePos) / FADE)
        : smoothstep((0.5 - Math.abs(slidePos - (index + 0.5))) / FADE);

    const e = smoothstep(presence);
    g.visible = e > 0.005;
    if (!g.visible) return;

    // Slide 0 zooms from small → large as you scroll through it; the rest do a
    // gentle scale-up as they fade in.
    const scale =
      index === 0
        ? THREE.MathUtils.lerp(
            0.55,
            1.2,
            smoothstep(THREE.MathUtils.clamp(slidePos, 0, 1)),
          )
        : THREE.MathUtils.lerp(0.85, 1.0, e);
    g.scale.setScalar(globalScale * scale);

    // Cross-fade by scaling every material's opacity against its authored value.
    g.traverse((o) => {
      if (!o.material) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const mat of mats) {
        if (!baseOpacity.current.has(mat))
          baseOpacity.current.set(mat, mat.opacity ?? 1);
        mat.transparent = true;
        mat.opacity = baseOpacity.current.get(mat) * e;
      }
    });
  });

  return <group ref={ref}>{children}</group>;
}

export default function AudioExperience() {
  const scroll = useScroll();
  const { size } = useThree();
  // The HUD's scene-info text block (title, description, "used in" pills) is
  // taller and more variable on mobile, so the fixed desktop origin sits the
  // scene too close underneath it and they sometimes overlap. Push the scene
  // down further on narrow viewports for guaranteed clearance.
  const origin = size.width < 768 ? [0, -2.1, 0] : [0, -1, 0];

  return (
    <>
      <ambientLight intensity={0.12} />
      <pointLight position={[0, 20, 0]} intensity={0.5} color="#4466ff" />

      <Slide index={0} scroll={scroll}>
        <Suspense fallback={null}>
          <Scene01Birth position={origin} scroll={scroll} sceneIndex={0} />
        </Suspense>
      </Slide>

      <EffectComposer>
        <Bloom
          intensity={1.2}
          luminanceThreshold={0.15}
          luminanceSmoothing={0.85}
          mipmapBlur
        />
        <Vignette offset={0.08} darkness={0.85} />
      </EffectComposer>
    </>
  );
}
